import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { KeenIcon } from '@/components';
import { Container } from '@/components/container';
import { ConfirmationModal } from '@/components/modal';
import { useConfirmationModal } from '@/hooks';
import recordTypeService from '@/services/recordType/recordTypeService';
import { RecordTypeModal } from './RecordTypeModal';
import { RecordTypeStagesModal } from './RecordTypeStagesModal';

const RecordTypes = () => {
  const navigate = useNavigate();
  const [recordTypes, setRecordTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stagesFor, setStagesFor] = useState(null);
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();

  const fetchRecordTypes = async () => {
    setLoading(true);
    try {
      const { data, status } = await recordTypeService.getAll();
      if (status) setRecordTypes(data || []);
    } catch {
      toast.error('Failed to load record types.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecordTypes();
  }, []);

  const openCreate = () => {
    setModalMode('create');
    setEditData(null);
    setModalOpen(true);
  };

  const openEdit = (type) => {
    setModalMode('edit');
    setEditData(type);
    setModalOpen(true);
  };

  const handleSubmit = async (values, helpers) => {
    setSaving(true);
    try {
      if (modalMode === 'edit' && editData) {
        await recordTypeService.update(values, editData.id);
        toast.success('Record type updated.');
      } else {
        await recordTypeService.create(values);
        toast.success('Record type created.');
      }
      setModalOpen(false);
      helpers?.resetForm?.();
      fetchRecordTypes();
    } catch (error) {
      if (error?.errors) helpers?.setErrors?.(error.errors);
      toast.error(error?.message || 'Failed to save record type.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const id = actionId;
    closeModal();
    try {
      const { status } = await recordTypeService.remove(id);
      if (status) {
        toast.success('Record type deleted.');
        fetchRecordTypes();
      }
    } catch (error) {
      toast.warning(error?.message || 'This record type cannot be deleted.');
    }
  };

  return (
    <Container>
      <div className="mb-4 card">
        <div className="flex items-center justify-between card-header">
          <h3 className="card-title">Record Types</h3>
          <button className="btn btn-sm btn-primary" onClick={openCreate}>
            <KeenIcon icon="plus" /> New Record Type
          </button>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="py-8 text-sm text-center text-gray-500">Loading…</div>
          ) : recordTypes.length === 0 ? (
            <div className="py-8 text-sm text-center text-gray-500">
              No record types yet. Create your first one to start organizing matters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-auto">
                <thead>
                  <tr className="text-left border-b text-gray-500 text-xs uppercase">
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4 text-center">Stages</th>
                    <th className="py-2 pr-4 text-center">Fields</th>
                    <th className="py-2 pr-4 text-center">Matters</th>
                    <th className="py-2 pr-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recordTypes.map((type) => (
                    <tr
                      key={type.id}
                      className="border-b cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/account/home/record-types/${type.id}`)}
                    >
                      <td className="py-3 pr-4">
                        <span className="inline-flex items-center gap-2">
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: type.color || '#6B7280' }}
                          />
                          <span className="font-medium text-gray-900">{type.name}</span>
                          {type.is_default ? (
                            <span className="badge badge-sm badge-light">Default</span>
                          ) : null}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <span className={`badge badge-sm badge-outline ${type.is_active ? 'badge-success' : 'badge-secondary'}`}>
                          {type.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-center">{type.stages_count ?? 0}</td>
                      <td className="py-3 pr-4 text-center">{type.fields_count ?? 0}</td>
                      <td className="py-3 pr-4 text-center">{type.matters_count ?? 0}</td>
                      <td className="py-3 pr-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1">
                          <button
                            className="btn btn-xs btn-light"
                            onClick={() => navigate(`/account/home/record-types/${type.id}`)}
                          >
                            <KeenIcon icon="notepad-edit" /> Layout
                          </button>
                          <button className="btn btn-xs btn-light" onClick={() => setStagesFor(type)}>
                            <KeenIcon icon="row-horizontal" /> Stages
                          </button>
                          <button className="btn btn-xs btn-light" onClick={() => openEdit(type)}>
                            <KeenIcon icon="gear" /> Details
                          </button>
                          <button
                            className="btn btn-xs btn-light btn-outline"
                            disabled={type.is_default || (type.matters_count ?? 0) > 0}
                            title={
                              type.is_default
                                ? 'The default record type cannot be deleted.'
                                : (type.matters_count ?? 0) > 0
                                ? 'In use by existing matters. Deactivate instead.'
                                : 'Delete'
                            }
                            onClick={() => openModal(type.id)}
                          >
                            <KeenIcon icon="trash" className="text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <RecordTypeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        data={editData}
        onSubmit={handleSubmit}
        loading={saving}
      />

      {stagesFor ? (
        <RecordTypeStagesModal
          recordType={stagesFor}
          onClose={() => setStagesFor(null)}
          onChanged={fetchRecordTypes}
        />
      ) : null}

      <ConfirmationModal
        show={isOpen}
        onClose={closeModal}
        onConfirm={handleDelete}
        btnText="Delete"
        message="Are you sure you want to delete this record type? Its fields will become global matter fields."
        title="Are You Sure?"
      />
    </Container>
  );
};

export { RecordTypes };
